import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { httpsCallable, getFunctions } from 'firebase/functions'
import { getDatabase, ref, get } from 'firebase/database'
import useCustomerDatabase from '../hooks/useCustomerDatabase'
import useGetApiKeys from '../hooks/useGetApiKeys'
import useGetTokens from '../hooks/useGetTokens'
import useActivityLog from '../hooks/useActivityLog'
import Button from '../components/Button'
import InputField from '../components/InputField'
import SelectBox from '../components/SelectBox'
import { useToast } from '../context/ToastContext'

// Consistent section card: a titled, optionally-described white panel used to
// group related controls so the page reads as distinct sections rather than a
// flat stack of same-weight boxes.
const Section = ({ title, description, actions, children, className = '' }) => (
  <section className={`rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}>
    <div className='flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-3'>
      <div>
        <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
        {description && <p className='mt-0.5 text-xs text-gray-500'>{description}</p>}
      </div>
      {actions && <div className='shrink-0'>{actions}</div>}
    </div>
    <div className='px-5 py-4'>{children}</div>
  </section>
)

const CustomerEditPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { customer, addEmail, removeEmail, addApiKeyId, removeApiKeyId, setAccessToken, setIsMediproCustomer, setIsAcaproCustomer, setSupportPortalLink, setSupportTicketsEnabled, removeCustomer } = useCustomerDatabase(id)
  const { keys: allApiKeys, loading: keysLoading } = useGetApiKeys()
  const { tokens: allTokens, loading: tokensLoading } = useGetTokens()
  const { logAction } = useActivityLog()
  const [tokenValue, setTokenValue] = useState('')
  const [selectedKey, setSelectedKey] = useState('')
  const [createKeyEnv, setCreateKeyEnv] = useState('')
  const [createKeyName, setCreateKeyName] = useState('')
  const [isCreatingKey, setIsCreatingKey] = useState(false)
  const [usagePlans, setUsagePlans] = useState([])
  const [usagePlansLoading, setUsagePlansLoading] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState({})
  const [isAssigning, setIsAssigning] = useState({})
  const [keyAssignments, setKeyAssignments] = useState({})
  const [isDeletingKey, setIsDeletingKey] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAddKeyModal, setShowAddKeyModal] = useState(false)
  const [showAssignTokenModal, setShowAssignTokenModal] = useState(false)
  const [showAddEmailModal, setShowAddEmailModal] = useState(false)
  const [newEmailValue, setNewEmailValue] = useState('')
  const [welcomeTemplate, setWelcomeTemplate] = useState(null)
  const [supportPortalLinkValue, setSupportPortalLinkValue] = useState('')

  // Fetch welcome email template
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const snap = await get(ref(getDatabase(), 'EmailTemplates/customerWelcome'))
        if (snap.val()) setWelcomeTemplate(snap.val())
      } catch (err) {
        console.error('Could not load welcome template:', err)
      }
    }
    fetchTemplate()
  }, [])

  // Sync support portal link field with customer data
  useEffect(() => {
    if (customer?.supportPortalLink) {
      setSupportPortalLinkValue(customer.supportPortalLink)
    }
  }, [customer?.supportPortalLink])

  // Fetch usage plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const getPlans = httpsCallable(getFunctions(), 'getUsagePlans')
        const res = await getPlans()
        setUsagePlans(res.data || [])
      } catch (err) {
        console.error('Error fetching usage plans:', err)
      }
      setUsagePlansLoading(false)
    }
    fetchPlans()
  }, [])

  // Fetch current usage plan assignments for each key
  const fetchKeyAssignments = async (keyId) => {
    try {
      const getAssignments = httpsCallable(getFunctions(), 'getKeyUsagePlans')
      const res = await getAssignments({ keyId })
      setKeyAssignments((prev) => ({ ...prev, [keyId]: res.data || [] }))
    } catch (err) {
      console.error('Error fetching key assignments:', err)
    }
  }

  useEffect(() => {
    if (!keysLoading && customer?.apiKeyIds) {
      customer.apiKeyIds.forEach((keyId) => fetchKeyAssignments(keyId))
    }
  }, [keysLoading, customer?.apiKeyIds])

  // Helper to resolve key ID to name for display
  const getKeyName = (keyId) => {
    const keyObj = allApiKeys.find((k) => k.id === keyId)
    return keyObj ? keyObj.name : keyId
  }

  if (!customer) {
    return <div className='p-4'>Loading customer...</div>
  }

  const handleAddEmail = async (email) => {
    try {
      await addEmail(id, email)
      await logAction('add_email', { customer: customer.name, email })
      showToast('Email added', 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to add email', 'red')
    }
  }

  const handleRemoveEmail = async (email) => {
    try {
      await removeEmail(id, email)
      await logAction('remove_email', { customer: customer.name, email })
      showToast('Email removed', 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to remove email', 'red')
    }
  }

  const handleAddKeyId = async (keyId) => {
    try {
      await addApiKeyId(id, keyId)
      const name = getKeyName(keyId)
      await logAction('assign_api_key', { customer: customer.name, keyName: name })
      showToast(`API Key "${name}" added`, 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to add API Key', 'red')
    }
  }

  const handleRemoveKeyId = async (keyId) => {
    try {
      await removeApiKeyId(id, keyId)
      await logAction('unassign_api_key', { customer: customer.name, keyName: getKeyName(keyId) })
      showToast('API Key removed', 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to remove API Key', 'red')
    }
  }

  const handleSetToken = async () => {
    if (!tokenValue.trim()) return
    try {
      await setAccessToken(id, tokenValue.trim())
      await logAction('assign_token', { customer: customer.name, tokenId: tokenValue.trim() })
      showToast('Access token assigned', 'green')
      setTokenValue('')
    } catch (err) {
      console.error(err)
      showToast('Failed to assign token', 'red')
    }
  }

  const handleDelete = async () => {
    setShowDeleteConfirm(false)
    try {
      await removeCustomer(id)
      await logAction('delete_customer', { customer: customer.name })
      showToast('Customer deleted', 'green')
      navigate('/customers')
    } catch (err) {
      console.error(err)
      showToast('Failed to delete customer', 'red')
    }
  }

  const handleSaveSupportPortalLink = async () => {
    try {
      await setSupportPortalLink(id, supportPortalLinkValue.trim())
      await logAction('set_support_portal_link', { customer: customer.name, link: supportPortalLinkValue.trim() })
      showToast('Support portal link saved', 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to save support portal link', 'red')
    }
  }

  const handleCreateApiKey = async () => {
    if (!createKeyEnv) return
    setIsCreatingKey(true)
    try {
      const createKey = httpsCallable(getFunctions(), 'createApiKey')
      const finalName = createKeyName.trim() || undefined
      const res = await createKey({ keyName: finalName, customerName: customer.name, environment: createKeyEnv })
      await addApiKeyId(id, res.data.id)
      await logAction('create_api_key', { customer: customer.name, keyName: res.data.name, environment: createKeyEnv })
      showToast(`API Key "${res.data.name}" created and assigned`, 'green')
      setCreateKeyEnv('')
      setCreateKeyName('')
    } catch (err) {
      console.error(err)
      showToast('Failed to create API key', 'red')
    }
    setIsCreatingKey(false)
  }

  const handleDeleteApiKey = async (keyId) => {
    const name = getKeyName(keyId)
    if (!window.confirm(`Delete API key "${name}" from AWS? This cannot be undone.`)) return
    setIsDeletingKey({ ...isDeletingKey, [keyId]: true })
    try {
      const deleteFn = httpsCallable(getFunctions(), 'deleteApiKey')
      await deleteFn({ keyId })
      await removeApiKeyId(id, keyId)
      await logAction('delete_api_key', { customer: customer.name, keyName: name })
      showToast(`API Key "${name}" deleted`, 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to delete API key', 'red')
    }
    setIsDeletingKey({ ...isDeletingKey, [keyId]: false })
  }

  const handleAssignUsagePlan = async (keyId) => {
    const planId = selectedPlan[keyId]
    if (!planId) return
    setIsAssigning({ ...isAssigning, [keyId]: true })
    try {
      const assignFn = httpsCallable(getFunctions(), 'assignKeyToUsagePlan')
      await assignFn({ keyId, usagePlanId: planId })
      const planName = usagePlans.find((p) => p.id === planId)?.name || planId
      await logAction('assign_usage_plan', { customer: customer.name, keyName: getKeyName(keyId), planName })
      showToast(`Key assigned to plan "${planName}"`, 'green')
      setSelectedPlan({ ...selectedPlan, [keyId]: '' })
      fetchKeyAssignments(keyId)
    } catch (err) {
      console.error(err)
      showToast('Failed to assign usage plan', 'red')
    }
    setIsAssigning({ ...isAssigning, [keyId]: false })
  }

  const handleRemoveFromPlan = async (keyId, usagePlanId, planName) => {
    try {
      const removeFn = httpsCallable(getFunctions(), 'removeKeyFromUsagePlan')
      await removeFn({ keyId, usagePlanId })
      await logAction('remove_usage_plan', { customer: customer.name, keyName: getKeyName(keyId), planName })
      showToast(`Removed from plan "${planName}"`, 'green')
      fetchKeyAssignments(keyId)
    } catch (err) {
      console.error(err)
      showToast('Failed to remove from usage plan', 'red')
    }
  }

  return (
    <div className='mx-auto max-w-4xl p-4 text-left'>
      <div className='mb-6'>
        <button className='text-sm text-blue-6 hover:underline' onClick={() => navigate('/customers')}>← Back to Customers</button>
        <h1 className='mt-2 text-3xl font-bold text-gray-900'>{customer.name}</h1>
        <p className='mt-1 text-sm text-gray-500'>Manage this customer's emails, API keys, access, and features.</p>
      </div>

      <div className='space-y-6'>
        {/* Emails */}
        <Section
          title='Authorized Emails'
          description='Users allowed to sign in on behalf of this customer.'
          actions={
            <div className='flex items-center gap-2'>
              <button
                className='rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50'
                onClick={() => {
                  const defaultBody = 'Hi [name],\n\nYou\'ve been set up with access to the Inspire Health Tech Self-Service portal.\n\nVisit: https://selfservice.inspireht.com'
                  const body = welcomeTemplate ? welcomeTemplate.body.replace(/\[name\]/g, customer.name) : defaultBody.replace(/\[name\]/g, customer.name)
                  navigator.clipboard.writeText(body)
                  showToast('Invitation email copied', 'green')
                }}
              >
                Copy Invitation Email
              </button>
              <Button onClick={() => setShowAddEmailModal(true)}>Add</Button>
            </div>
          }
        >
          <ul className='divide-y divide-gray-100 rounded-md border border-gray-100'>
            {(customer.emails || []).map((email, i) => (
              <li key={i} className='flex items-center justify-between gap-2 px-3 py-2 text-sm'>
                <span className='text-gray-700'>{email}</span>
                <button className='text-xs text-red-600 hover:underline' onClick={() => handleRemoveEmail(email)}>Remove</button>
              </li>
            ))}
            {(!customer.emails || customer.emails.length === 0) && (
              <li className='px-3 py-2 text-sm text-gray-400'>None added yet.</li>
            )}
          </ul>
        </Section>

        {/* API Keys — each key is a card with inline usage plan management */}
        <Section
          title='API Keys'
          description='Assign existing keys or create new ones, and manage usage plan membership.'
          actions={
            <Button onClick={() => setShowAddKeyModal(true)}>Add</Button>
          }
        >
          <p className='mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700'>
            Unassigning an API key does not remove customer access to the API — delete the API key or remove any Usage Groups to prevent access with that key.
          </p>

          {(customer.apiKeyIds || []).map((keyId) => (
            <div key={keyId} className='mb-3 rounded-md border border-gray-200 bg-gray-50 p-3'>
              <div className='mb-2 flex items-center justify-between'>
                <span className='text-sm font-semibold text-gray-800'>{getKeyName(keyId)}</span>
                <div className='flex gap-3'>
                  <button className='text-xs text-gray-500 hover:underline' onClick={() => handleRemoveKeyId(keyId)}>Unassign</button>
                  <button
                    className='text-xs text-red-600 hover:underline'
                    onClick={() => handleDeleteApiKey(keyId)}
                    disabled={isDeletingKey[keyId]}
                  >
                    {isDeletingKey[keyId] ? 'Deleting...' : 'Delete from AWS'}
                  </button>
                </div>
              </div>

              {/* Inline usage plans — single row */}
              <div className='ml-2 flex items-center flex-wrap gap-1 mt-1'>
                <span className='text-xs text-gray-500 mr-1'>Plans:</span>
                {keyAssignments[keyId] && keyAssignments[keyId].map((a) => (
                  <span key={a.usagePlanId} className='inline-flex items-center bg-blue-100 text-blue-800 text-xs rounded px-2 py-0.5'>
                    {a.usagePlanName}
                    <button className='ml-1 text-red-500 hover:text-red-700' onClick={() => handleRemoveFromPlan(keyId, a.usagePlanId, a.usagePlanName)}>×</button>
                  </span>
                ))}
                {keyAssignments[keyId] && keyAssignments[keyId].length === 0 && (
                  <span className='text-xs text-gray-400'>None</span>
                )}
                {!keyAssignments[keyId] && <span className='text-xs text-gray-400'>Loading...</span>}
                {!usagePlansLoading && (
                  <>
                    <select
                      className='text-xs border border-gray-200 rounded p-1 ml-1'
                      value={selectedPlan[keyId] || ''}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, [keyId]: e.target.value })}
                    >
                      <option value=''>+ Add</option>
                      {usagePlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>{plan.name}</option>
                      ))}
                    </select>
                    {selectedPlan[keyId] && (
                      <button
                        className='text-xs text-blue-6 hover:underline'
                        onClick={() => handleAssignUsagePlan(keyId)}
                        disabled={isAssigning[keyId]}
                      >
                        {isAssigning[keyId] ? '...' : 'Add'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {(!customer.apiKeyIds || customer.apiKeyIds.length === 0) && (
            <p className='mb-3 text-sm text-gray-500'>No API keys assigned yet.</p>
          )}

        </Section>

        {/* Access Token */}
        <Section
          title='Access Token'
          description='The documentation access token assigned to this customer.'
          actions={
            <Button onClick={() => setShowAssignTokenModal(true)}>Add</Button>
          }
        >
          <p className='text-sm'>
            Current: {customer.accessToken
            ? <span className='rounded bg-gray-100 px-2 py-0.5 font-mono text-sm'>{allTokens.find((t) => t.id === customer.accessToken)?.name || customer.accessToken}</span>
            : <span className='text-gray-500'>None assigned</span>}
            {customer.accessToken && (
              <button
                className='ml-2 text-xs text-red-600 hover:underline'
                onClick={async () => {
                  try { await setAccessToken(id, null); await logAction('remove_token', { customer: customer.name }); showToast('Token removed', 'green') } catch (err) { showToast('Failed', 'red') }
                }}
              >
                Remove
              </button>
            )}
          </p>
        </Section>

        {/* Support Portal Link */}
        <Section
          title='Support Portal Link'
          description='If set, the customer sees a button on their dashboard linking to this URL.'
        >
          <div className='flex items-end gap-2'>
            <InputField
              containerClassName='flex-1'
              type='url'
              onChange={(e) => setSupportPortalLinkValue(e.target.value)}
              value={supportPortalLinkValue}
              placeholder='https://support.example.com'
            />
            <Button onClick={handleSaveSupportPortalLink}>Save</Button>
            {customer.supportPortalLink && (
              <button
                className='text-xs text-red-600 hover:underline'
                onClick={async () => {
                  try {
                    await setSupportPortalLink(id, null)
                    setSupportPortalLinkValue('')
                    await logAction('remove_support_portal_link', { customer: customer.name })
                    showToast('Support portal link removed', 'green')
                  } catch (err) {
                    showToast('Failed to remove link', 'red')
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        </Section>

        {/* Feature Access */}
        <Section
          title='Feature Access'
          description='Toggle which product areas and features this customer can use.'
        >
          <div className='divide-y divide-gray-100 rounded-md border border-gray-100'>
            <label className='flex cursor-pointer items-start gap-3 p-3'>
              <input
                type='checkbox'
                checked={customer.isMediproCustomer !== false}
                onChange={async (e) => {
                  try {
                    await setIsMediproCustomer(id, e.target.checked)
                    await logAction('toggle_medipro_customer', { customer: customer.name, enabled: e.target.checked })
                    showToast(`Medipro Customer ${e.target.checked ? 'enabled' : 'disabled'}`, 'green')
                  } catch (err) {
                    showToast('Failed to update', 'red')
                  }
                }}
                className='mt-0.5 h-4 w-4'
              />
              <span className='text-sm'>
                <span className='font-medium text-gray-800'>Medipro Customer</span>
                <span className='block text-xs text-gray-500'>Grants access to the Medipro Browser and MediPro analytics.</span>
              </span>
            </label>
            <label className='flex cursor-pointer items-start gap-3 p-3'>
              <input
                type='checkbox'
                checked={customer.isAcaproCustomer || false}
                onChange={async (e) => {
                  try {
                    await setIsAcaproCustomer(id, e.target.checked)
                    await logAction('toggle_acapro_customer', { customer: customer.name, enabled: e.target.checked })
                    showToast(`ACAPro Customer ${e.target.checked ? 'enabled' : 'disabled'}`, 'green')
                  } catch (err) {
                    showToast('Failed to update', 'red')
                  }
                }}
                className='mt-0.5 h-4 w-4'
              />
              <span className='text-sm'>
                <span className='font-medium text-gray-800'>ACAPro Customer</span>
                <span className='block text-xs text-gray-500'>Grants access to ACAPro analytics.</span>
              </span>
            </label>
            <label className='flex cursor-pointer items-start gap-3 p-3'>
              <input
                type='checkbox'
                checked={customer.supportTicketsEnabled || false}
                onChange={async (e) => {
                  try {
                    await setSupportTicketsEnabled(id, e.target.checked)
                    await logAction('toggle_support_tickets', { customer: customer.name, enabled: e.target.checked })
                    showToast(`Support Tickets ${e.target.checked ? 'enabled' : 'disabled'}`, 'green')
                  } catch (err) {
                    showToast('Failed to update', 'red')
                  }
                }}
                className='mt-0.5 h-4 w-4'
              />
              <span className='text-sm'>
                <span className='font-medium text-gray-800'>Support Tickets</span>
                <span className='block text-xs text-gray-500'>Shows the Support navigation link so this customer can submit and view support tickets.</span>
              </span>
            </label>
          </div>
        </Section>

        {/* Danger Zone */}
        <Section title='Danger Zone' className='border-red-200'>
          <div className='flex items-center justify-between gap-4'>
            <p className='text-sm text-gray-600'>Permanently delete this customer record. This cannot be undone.</p>
            <Button className='bg-red-600 hover:bg-red-700' onClick={() => setShowDeleteConfirm(true)}>Delete Customer</Button>
          </div>
        </Section>
      </div>

      {/* Add Email Modal */}
      {showAddEmailModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50' onClick={() => { setShowAddEmailModal(false); setNewEmailValue('') }}>
          <div className='mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg' role='dialog' aria-labelledby='add-email-title' onClick={(e) => e.stopPropagation()}>
            <div className='mb-4 flex items-center justify-between'>
              <h3 id='add-email-title' className='text-lg font-semibold text-gray-900'>Add Authorized Email</h3>
              <button className='text-2xl leading-none text-gray-500 hover:text-gray-700' onClick={() => { setShowAddEmailModal(false); setNewEmailValue('') }}>&times;</button>
            </div>
            <form
              className='flex items-end gap-2'
              onSubmit={async (e) => {
                e.preventDefault()
                const email = newEmailValue.trim()
                if (!email) return
                await handleAddEmail(email)
                setNewEmailValue('')
                setShowAddEmailModal(false)
              }}
            >
              <InputField
                containerClassName='flex-1'
                type='email'
                onChange={(e) => setNewEmailValue(e.target.value)}
                value={newEmailValue}
                placeholder='user@example.com'
              />
              <Button type='submit' disabled={!newEmailValue.trim()}>Add</Button>
            </form>
          </div>
        </div>
      )}

      {/* Assign Token Modal */}
      {showAssignTokenModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50' onClick={() => setShowAssignTokenModal(false)}>
          <div className='mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg' role='dialog' aria-labelledby='assign-token-title' onClick={(e) => e.stopPropagation()}>
            <div className='mb-4 flex items-center justify-between'>
              <h3 id='assign-token-title' className='text-lg font-semibold text-gray-900'>Assign Access Token</h3>
              <button className='text-2xl leading-none text-gray-500 hover:text-gray-700' onClick={() => setShowAssignTokenModal(false)}>&times;</button>
            </div>
            <div className='flex items-end gap-2'>
              <SelectBox
                label='Assign Token'
                name='tokenSelect'
                value={tokenValue}
                onChange={(e) => setTokenValue(e.target.value)}
              >
                <option value=''>-- Select --</option>
                {!tokensLoading && allTokens.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </SelectBox>
              <Button onClick={async () => { await handleSetToken(); if (tokenValue.trim()) setShowAssignTokenModal(false) }}>Assign</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add API Key Modal — assign an existing key or create a new one */}
      {showAddKeyModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50' onClick={() => setShowAddKeyModal(false)}>
          <div className='mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg' role='dialog' aria-labelledby='add-key-title' onClick={(e) => e.stopPropagation()}>
            <div className='mb-4 flex items-center justify-between'>
              <h3 id='add-key-title' className='text-lg font-semibold text-gray-900'>Add API Key</h3>
              <button className='text-2xl leading-none text-gray-500 hover:text-gray-700' onClick={() => setShowAddKeyModal(false)}>&times;</button>
            </div>

            {/* Assign existing key */}
            <div className='mb-4'>
              <p className='mb-2 text-sm font-semibold text-gray-800'>Assign Existing Key</p>
              <div className='flex items-end gap-2'>
                <SelectBox
                  label=''
                  name='apiKeySelect'
                  value={selectedKey}
                  onChange={(e) => setSelectedKey(e.target.value)}
                >
                  <option value=''>-- Select --</option>
                  {!keysLoading && allApiKeys
                    .filter((k) => k.id !== 'all' && !(customer.apiKeyIds || []).includes(k.id))
                    .map((k) => (
                      <option key={k.id} value={k.id}>{k.name}</option>
                    ))}
                </SelectBox>
                <Button onClick={() => { if (selectedKey) { handleAddKeyId(selectedKey); setSelectedKey(''); setShowAddKeyModal(false) } }}>Assign</Button>
              </div>
            </div>

            {/* Create new key */}
            <div className='border-t border-gray-100 pt-4'>
              <p className='mb-2 text-sm font-semibold text-gray-800'>Create New Key</p>
              <div className='flex items-end gap-2'>
                <SelectBox
                  label='Environment'
                  name='createKeyEnv'
                  value={createKeyEnv}
                  onChange={(e) => {
                    setCreateKeyEnv(e.target.value)
                    if (!createKeyName || createKeyName === `${customer.name} - ${createKeyEnv} Key`) {
                      setCreateKeyName(e.target.value ? `${customer.name} - ${e.target.value} Key` : '')
                    }
                  }}
                >
                  <option value=''>-- Env --</option>
                  <option value='Dev'>Dev</option>
                  <option value='UAT'>UAT</option>
                  <option value='Prod'>Prod</option>
                </SelectBox>
                <InputField
                  containerClassName=''
                  type='text'
                  onChange={(e) => setCreateKeyName(e.target.value)}
                  value={createKeyName}
                  placeholder='Key name'
                />
                <Button
                  onClick={async () => { await handleCreateApiKey(); if (createKeyEnv) setShowAddKeyModal(false) }}
                  disabled={isCreatingKey || !createKeyEnv}
                >
                  {isCreatingKey ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg' role='dialog' aria-labelledby='delete-confirm-title'>
            <h3 id='delete-confirm-title' className='text-lg font-bold text-red-700 mb-2'>Delete Customer</h3>
            <p className='text-sm text-gray-700 mb-4'>
              Are you sure you want to delete <span className='font-bold'>{customer.name}</span>? This action cannot be undone.
            </p>
            <div className='flex justify-end gap-2'>
              <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button className='bg-red-600 hover:bg-red-700' onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerEditPage
